/**
 * `LED-01`'s words, exactly as the screen book writes them (`STD-002 §7`).
 *
 * They live here rather than in the JSX for the reason `FEAT-011 §9` gives: copy written into a component
 * is copy nobody reviews. Every string on this screen is a promise about somebody's money, and the trial
 * line in particular is a promise about when they will be charged.
 */
export const homeCopy = {
  wordmark: 'Track My Spends',

  /** The `+`. Named for a screen reader; the glyph is decoration. */
  addEntry: 'Add an entry',

  /**
   * The greeting, from the screen book's "Good evening, Asha · Sun, 16 Aug" (`FEAT-011 §2.1`).
   *
   * It is the only line on `LED-01` that addresses the person rather than their money, and it is read by
   * the hour where *they* are, not where the server is. The first word of the display name, because "Good
   * evening, Asha Rao" reads like a letter from a bank. No name until the account has loaded — the
   * greeting appears once, in its final shape, rather than gaining a name a beat later.
   */
  greeting: (hour: number, name: string | undefined, date: string): string => {
    const part = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
    const first = name?.trim().split(/\s+/)[0];

    return `${first ? `${part}, ${first}` : part} · ${date}`;
  },

  /** The month is appended by the screen, from what the server called the payday window. */
  balanceEyebrow: 'Balance',

  recentHeading: 'Recent entries',
  fullLedger: 'Full ledger →',

  shortcuts: [
    { id: 'budgets', label: '📊 Budgets' },
    { id: 'shopping', label: '🧺 Shopping list' },
    { id: 'reports', label: '📈 Reports' },
  ],

  /**
   * The first ledger anybody opens is empty, so this is one of the first things they read. It names the
   * next step rather than reporting an absence (`STD-002 §3.4`).
   */
  noEntriesTitle: 'Nothing recorded yet',
  noEntriesBody: 'Tap + to add your first entry. It takes about five seconds.',

  /**
   * The balance could not be fetched. It says which month, because "we could not load August" tells
   * somebody whether to try again or to look at their filter, and "we could not load" does not.
   */
  summaryFailed: 'We could not load this month. Check your connection and pull down to try again.',

  /**
   * `LED-01`'s trial strip. Honest about the count, and it blocks nothing — the screen book's own note on
   * this banner is that it "is honest about the count but never blocks anything".
   */
  trialHeadline: (days: number): string =>
    days === 0 ? 'Your free months end today' : days === 1 ? '1 free day left' : `${String(days)} free days left`,
  trialBody: 'Then ₹30/month. We will remind you before anything is charged.',
} as const;
